import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuthStore, useTestStore } from '../store';
import { localApi, type ActiveTest } from '../utils/local-api';
import { api } from '../utils/api';
import PinyinText from '../components/PinyinText';
import { progressAnswerCount, progressDigest, verifyProgressConfirmation } from '../utils/simulationProgress';
import {
  extractImagePath,
  getSequenceComposerTokens,
  hasIndependentGroupOptions,
  isImagePath,
  resolveQuestionImagePath,
  stripImagePathFromText,
} from '../utils/simulationQuestionPresentation';

export interface SimulationConfig {
  totalQuestions: number;
  title: string;
  hasWriting: boolean;
  description: string;
}

export interface ScoreSummary {
  total: number;
  max: number;
  listening: number;
  reading: number;
  writing: number;
}

export const UNKNOWN_ANSWER = -1;

const SIMULATION_CONFIG: Record<number, SimulationConfig> = {
  2: { totalQuestions: 60, title: 'HSK 2 全真模拟', hasWriting: false, description: 'Listening + Reading' },
  3: { totalQuestions: 80, title: 'HSK 3 全真模拟', hasWriting: true, description: 'Listening + Reading + Writing' },
  4: { totalQuestions: 100, title: 'HSK 4 全真模拟', hasWriting: true, description: 'Listening + Reading + Writing' },
};

const toMediaUrl = (path?: string | null) => {
  if (!path) return '';
  if (/^https?:\/\//i.test(path)) return path;
  return `${BACKEND_URL}/media/${path.replace(/^\/+/, '')}`;
};

const getQuestionGroup = (q: any) => q?.questionGroup ?? q?.question_group ?? null;
const getTokens = (question: any) => getSequenceComposerTokens(question);
const getTokenCounts = (tokens: string[]) => {
  const counts: Record<string, number> = {};
  tokens.forEach(t => counts[t] = (counts[t] || 0) + 1);
  return counts;
};

const sleep = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

const getWritingStorageKey = (testId?: string | null) =>
  testId ? `simulation-writing-answers:${testId}` : null;

const loadPersistedWritingAnswers = (testId?: string | null) => {
  const key = getWritingStorageKey(testId);
  if (!key || typeof window === 'undefined') return {};
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
};

const loadPersistedChoiceAnswers = (testId: string, questions: any[]) => {
  try {
    const saved = JSON.parse(window.localStorage.getItem(`simulation-choice-answers:${testId}`) || 'null');
    if (saved?.questionIds?.length === questions.length && questions.every((q, i) => q.id === saved.questionIds[i])
      && Array.isArray(saved.answers) && saved.answers.length === questions.length) return saved.answers as (number | null)[];
  } catch { /* Cloud state remains available if a local backup cannot be read. */ }
  return undefined;
};

const clearPersistedWritingAnswers = (testId?: string | null) => {
  const key = getWritingStorageKey(testId);
  if (!key || typeof window === 'undefined') return;
  window.localStorage.removeItem(key);
};

export function useSimulationController(examLevel: string | undefined) {
  const navigate = useNavigate();
  const location = useLocation();
  const [choiceState, setChoiceState] = useState<{ path: string; id: string | null }>(() => ({ path: location.pathname, id: new URLSearchParams(location.search).get('testId') }));
  const sessionChoice = choiceState.path === location.pathname ? choiceState.id : new URLSearchParams(location.search).get('testId');
  const [resumeChoices, setResumeChoices] = useState<ActiveTest[] | null>(null);
  const startRequests = useRef(new Map<string, Promise<any>>());
  const { token } = useAuthStore();
  const {
    currentTest: storeCurrentTest,
    startTest,
    answerQuestion,
    nextQuestion,
    previousQuestion,
    clearCurrentTest,
  } = useTestStore();

  const parsedLevel = Number(String(examLevel || '').replace(/\D/g, ''));
  const HSK_LEVEL = parsedLevel === 2 || parsedLevel === 3 || parsedLevel === 4 ? parsedLevel : 3;
  const TEST_PART = 'simulation';
  const currentConfig = SIMULATION_CONFIG[HSK_LEVEL];
  const TOTAL_QUESTIONS = currentConfig.totalQuestions;
  const shouldShowPinyin = HSK_LEVEL === 2;
  const examScope = `hsk:2.0:simulation:${HSK_LEVEL}:simulation:`;
  const currentTest = storeCurrentTest?.examScope === examScope ? storeCurrentTest : null;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showResults, setShowResults] = useState(false);
  const [initializingApi, setInitializingApi] = useState(true);
  const [isCloudflare, setIsCloudflare] = useState(false);
  const [cloudSessionId, setCloudSessionId] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [scoreSummary, setScoreSummary] = useState<ScoreSummary | null>(null);

  const [isPlaying, setIsPlaying] = useState(false);
  const [audioElement, setAudioElement] = useState<HTMLAudioElement | null>(null);
  const activeAudioRef = useRef<HTMLAudioElement | null>(null);
  const [audioPlayedMap, setAudioPlayedMap] = useState<Record<string, boolean>>({});
  const [groupSelections, setGroupSelections] = useState<number[]>([]);

  const [writingAnswers, setWritingAnswers] = useState<Record<number, { sequence?: string[]; text?: string }>>({});
  const writingAnswersRef = useRef<Record<number, { sequence?: string[]; text?: string }>>({});
  const localEditSerial = useRef(0);
  const cloudProgressRevision = useRef(0);
  const autoSaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [writingSessionId, setWritingSessionId] = useState<string | null>(null);
  const progressSaveQueue = useRef<Promise<unknown>>(Promise.resolve());
  const [dragIndex, setDragIndex] = useState<number | null>(null);

  const [isCheckpointSaving, setIsCheckpointSaving] = useState(false);
  const [isManualSaving, setIsManualSaving] = useState(false);
  const [checkpointError, setCheckpointError] = useState('');
  const [saveProgressMessage, setSaveProgressMessage] = useState('');
  const [saveProgressComplete, setSaveProgressComplete] = useState(false);

  const updateWritingAnswers = (next: Record<number, { sequence?: string[]; text?: string }>) => {
    localEditSerial.current++;
    writingAnswersRef.current = next;
    setWritingAnswers(next);
    setSaveProgressMessage('');
    const key = getWritingStorageKey(currentTest?.testId);
    if (key && writingSessionId === currentTest?.testId) {
      try { window.localStorage.setItem(key, JSON.stringify(next)); }
      catch { setCheckpointError('本机备份写入失败，请保持页面打开并保存到云端。'); }
    }
  };

  // --- Type helpers ---
  const isListeningPart = (q: any) => {
    if (!q) return false;
    return q.part && (q.part.toLowerCase().includes('listening') || q.part.startsWith('L'));
  };
  const isReadingPart = (q: any) => {
    if (!q) return false;
    return q.part && (q.part.toLowerCase().includes('reading') || q.part.startsWith('R'));
  };
  const isWritingPart = (q: any) => {
    if (!q) return false;
    return q.part && (q.part.toLowerCase().includes('writing') || q.part.startsWith('W'));
  };
  const isSequenceQuestion = (q: any) => {
    if (!q) return false;
    const part = String(q.part || '').toUpperCase();
    const level = Number(q.level ?? HSK_LEVEL);
    return part === 'W1' || (level === 4 && part === 'R3');
  };
  const isLongFormWritingQuestion = (q: any) => {
    if (!q) return false;
    const part = String(q.part || '').toUpperCase();
    const level = Number(q.level ?? HSK_LEVEL);
    return level === 4 && part === 'W2';
  };
  const isTextEntryQuestion = (q: any) => isWritingPart(q) && !isSequenceQuestion(q);
  const isVerticalSequenceQuestion = (q: any) => {
    if (!q) return false;
    return Number(q.level ?? HSK_LEVEL) === 4 && String(q.part || '').toUpperCase() === 'R3';
  };

  const renderText = (text: string, className = '', pinyinClassName = ''): React.ReactNode => {
    if (shouldShowPinyin) {
      return React.createElement(PinyinText, { text, className, pinyinClassName });
    }
    return React.createElement('span', { className }, text);
  };

  // Browser navigation can select another session while this route remains mounted.
  useEffect(() => {
    const id = new URLSearchParams(location.search).get('testId');
    if (!id || id !== useTestStore.getState().currentTest?.testId) setChoiceState({ path: location.pathname, id });
  }, [location.pathname, location.search]);

  // --- Data loading ---
  const waitForHistoryRecord = async (testId: string, authToken: string) => {
    for (let attempt = 0; attempt < 5; attempt++) {
      const history = await api.getHistory(authToken);
      if ((history || []).some((item: any) => item.id === testId)) return true;
      if (attempt < 4) await sleep(800);
    }
    return false;
  };

  useEffect(() => {
    if (!token) {
      navigate('/');
      return;
    }

    let cancelled = false;
    const initializeAndLoadQuestions = async () => {
      try {
        setInitializingApi(true);
        setLoading(true);
        setWritingSessionId(null);
        setError('');
        setResumeChoices(null);
        let selection = sessionChoice;
        if (!selection) {
          const active = await localApi.getActiveTests(token);
          if (cancelled) return;
          const matching = active.active.filter(test => Number(test.level) === HSK_LEVEL && test.part === TEST_PART
            && (test.bank_version || '2.0') === '2.0' && (test.system_type || 'simulation') === 'simulation');
          if (matching.length) { setResumeChoices(matching); return; }
          selection = 'new';
        }
        const sessionOptions = selection === 'new' ? { start_new: true } : { test_session_id: selection };
        const startOnce = (cloud: boolean) => {
          const key = `${token}:${HSK_LEVEL}:${selection}:${cloud}`;
          if (!startRequests.current.has(key)) {
            const pending = cloud
              ? api.startTest(HSK_LEVEL, TEST_PART, TOTAL_QUESTIONS, token, undefined, sessionOptions)
              : localApi.startTest(HSK_LEVEL, TEST_PART, TOTAL_QUESTIONS, token, undefined, undefined, sessionOptions);
            startRequests.current.set(key, pending.then(value => { startRequests.current.delete(key); return value; }, error => { startRequests.current.delete(key); throw error; }));
          }
          return startRequests.current.get(key)!;
        };
        const recordSessionUrl = (id: string) => {
          setChoiceState({ path: location.pathname, id });
          navigate(`${location.pathname}?testId=${encodeURIComponent(id)}`, { replace: true });
        };

        const isMatchingTest = currentTest &&
          currentTest.questions &&
          currentTest.questions.length > 0 &&
          currentTest.questions.length === TOTAL_QUESTIONS &&
          currentTest.part === TEST_PART &&
          currentTest.level === HSK_LEVEL;

        // Always reconcile with the server: a cached empty session must not
        // hide an older saved exam. Keep local-only writing answers for the
        // same immutable question order so a phone can recover unsent work.
        const localWriting = isMatchingTest ? loadPersistedWritingAnswers(currentTest!.testId) : {};
        if (isMatchingTest) updateWritingAnswers(localWriting);

		if (!isMatchingTest && currentTest && currentTest.part === TEST_PART && currentTest.level === HSK_LEVEL) {
			clearCurrentTest();
		}

        const host = typeof window !== 'undefined' ? window.location.host : '';
        const cf = /workers\.dev|pages\.dev/i.test(host);
        setIsCloudflare(cf);
        if (cf) {
          const res = await startOnce(true) as Awaited<ReturnType<typeof api.startTest>>;
          if (cancelled) return;
          setCloudSessionId(res.test_session_id);
          const questions = res.questions.map((q: any) => ({
            id: q.question_id,
            level: q.level ?? HSK_LEVEL,
            part: q.part,
            question: q.question_text,
            options: q.options,
            correctAnswer: -1,
            media: q.media,
            order: q.order,
            explanation: q.explanation,
            questionGroup: q.question_group ?? null,
            groupSeq: q.group_seq ?? null,
          }));

          const sameLocalTest = isMatchingTest && currentTest!.testId === res.test_session_id
            && questions.every((q: any, i: number) => q.id === currentTest!.questions[i]?.id);
          let initialIndex = sameLocalTest ? currentTest!.currentQuestionIndex : 0;
          let initialAnswers: (number | null)[] | undefined = sameLocalTest ? [...currentTest!.answers] : loadPersistedChoiceAnswers(res.test_session_id, questions);
          const waInit: Record<number, { sequence?: string[]; text?: string }> = { ...loadPersistedWritingAnswers(res.test_session_id), ...(sameLocalTest ? localWriting : {}) };

          if (res.resumed && res.progress) {
            initialIndex = res.progress.index || 0;
            if (res.progress.answers) {
              initialAnswers = initialAnswers || new Array(questions.length).fill(null);
              Object.entries(res.progress.answers).forEach(([idxStr, ans]) => {
                const idx = Number(idxStr);
                if (idx >= 0 && idx < questions.length) {
                  if (ans === null) {
                    initialAnswers![idx] = null;
                    if (isSequenceQuestion(questions[idx])) waInit[idx] = { sequence: [] };
                    else if (isTextEntryQuestion(questions[idx])) waInit[idx] = { text: '' };
                    return;
                  }
                  if (typeof ans === 'object' || typeof ans === 'string') {
                    if (Array.isArray(ans)) waInit[idx] = { sequence: ans as string[] };
                    else if (typeof ans === 'string') waInit[idx] = { text: String(ans) };
                  } else {
                    initialAnswers![idx] = ans as number;
                  }
                }
              });
            }
          }
          updateWritingAnswers(waInit);
          cloudProgressRevision.current = Number(res.progress?.revision || 0);
          startTest(HSK_LEVEL, TEST_PART, questions, res.test_session_id, initialIndex, initialAnswers, examScope);
          setWritingSessionId(res.test_session_id);
          recordSessionUrl(res.test_session_id);
          setLoading(false);
          setInitializingApi(false);
          return;
        }

        const response = await startOnce(false) as Awaited<ReturnType<typeof localApi.startTest>>;
        if (cancelled) return;
        if (!response.success) throw new Error('Failed to start simulation test');
        if (!response.questions || response.questions.length === 0) {
          throw new Error(`HSK ${HSK_LEVEL} 题库暂无题目，请联系管理员添加题目后再试。`);
        }

        const questions = response.questions.map(q => ({
          id: q.question_id,
          level: q.level ?? HSK_LEVEL,
          part: q.part,
          question: q.question_text,
          options: q.options,
          correctAnswer: q.correct_index,
          media: q.media ?? undefined,
          order: q.order,
          explanation: q.explanation ?? undefined,
          questionGroup: (q as any).question_group ?? null,
          groupSeq: (q as any).group_seq ?? null,
        }));

        const sameLocalTest = isMatchingTest && currentTest!.testId === response.test_id
          && questions.every((q, i) => q.id === currentTest!.questions[i]?.id);
        let initialIndex = sameLocalTest ? currentTest!.currentQuestionIndex : 0;
        let initialAnswers: (number | null)[] | undefined = sameLocalTest ? [...currentTest!.answers] : loadPersistedChoiceAnswers(response.test_id, questions);
        const waInit: Record<number, { sequence?: string[]; text?: string }> = { ...loadPersistedWritingAnswers(response.test_id), ...(sameLocalTest ? localWriting : {}) };

        if (response.resumed && response.progress) {
          initialIndex = response.progress.index || 0;
          if (response.progress.answers) {
            initialAnswers = initialAnswers || new Array(questions.length).fill(null);
            Object.entries(response.progress.answers).forEach(([idxStr, ans]) => {
              const idx = Number(idxStr);
              if (idx >= 0 && idx < questions.length) {
                if (ans === null) {
                  initialAnswers![idx] = null;
                  if (isSequenceQuestion(questions[idx])) waInit[idx] = { sequence: [] };
                  else if (isTextEntryQuestion(questions[idx])) waInit[idx] = { text: '' };
                  return;
                }
                if (typeof ans === 'object' || typeof ans === 'string') {
                  if (Array.isArray(ans)) waInit[idx] = { sequence: ans as string[] };
                  else if (typeof ans === 'string') waInit[idx] = { text: String(ans) };
                } else {
                  initialAnswers![idx] = ans as number;
                }
              }
            });
          }
        }
        updateWritingAnswers(waInit);
        cloudProgressRevision.current = Number(response.progress?.revision || 0);
        startTest(HSK_LEVEL, TEST_PART, questions, response.test_id, initialIndex, initialAnswers, examScope);
        setWritingSessionId(response.test_id);
        recordSessionUrl(response.test_id);
      } catch (err: any) {
        if (!cancelled) setError(err.message || `Failed to load HSK ${HSK_LEVEL} Simulation questions`);
      } finally {
        if (!cancelled) { setLoading(false); setInitializingApi(false); }
      }
    };

    initializeAndLoadQuestions();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, HSK_LEVEL, sessionChoice]);

  // Keep per-session local choice backups when another same-level session becomes current.
  useEffect(() => {
    if (!currentTest?.testId || writingSessionId !== currentTest.testId || loading || initializingApi) return;
    try { window.localStorage.setItem(`simulation-choice-answers:${currentTest.testId}`, JSON.stringify({
      questionIds: currentTest.questions.map(q => q.id), answers: currentTest.answers,
    })); } catch { setCheckpointError('本机备份写入失败，请保持页面打开并保存到云端。'); }
  }, [currentTest?.testId, currentTest?.answers, writingSessionId, loading, initializingApi]);

  // Persist writing answers to localStorage
  useEffect(() => {
    const key = getWritingStorageKey(currentTest?.testId);
    if (!key || writingSessionId !== currentTest?.testId || typeof window === 'undefined') return;
    try { window.localStorage.setItem(key, JSON.stringify(writingAnswers)); }
    catch { setCheckpointError('本机备份写入失败，请保持页面打开并保存到云端。'); }
  }, [currentTest?.testId, writingAnswers, writingSessionId]);

  const buildProgressAnswers = () => {
    const answersObj: Record<string, any> = {};
    const latestTest = useTestStore.getState().currentTest;
    const currentAnswers = latestTest?.testId === currentTest?.testId ? latestTest?.answers : currentTest?.answers;
    currentAnswers?.forEach((ans, i) => {
      if (ans !== null) answersObj[i] = ans;
    });
    const localWriting = loadPersistedWritingAnswers(currentTest?.testId);
    Object.entries({ ...localWriting, ...writingAnswersRef.current }).forEach(([k, v]) => {
      if (v.sequence?.length) answersObj[k] = v.sequence;
      else if (v.text?.trim().length) answersObj[k] = v.text;
      else if ('sequence' in v || 'text' in v) answersObj[k] = null;
    });
    return answersObj;
  };

  const saveProgress = async (options?: { showSuccess?: boolean; showError?: boolean }) => {
    if (!currentTest?.testId || writingSessionId !== currentTest.testId || !token || currentTest.testId.startsWith('practice-')) return false;
    setSaveProgressMessage('');
    try {
      const editSerial = localEditSerial.current;
      const answersObj = buildProgressAnswers();
      const questionIds = currentTest.questions.map(question => question.id);
      const pending = progressSaveQueue.current.catch(() => undefined).then(async () => {
        if (options?.showSuccess) {
          const current = await localApi.getSavedProgress(currentTest.testId!, token);
          if (!current.success || current.test_session_id !== currentTest.testId || !Number.isInteger(current.revision)
            || JSON.stringify(current.question_ids) !== JSON.stringify(questionIds)) {
            throw new Error('无法核对当前云端试卷，本机答案仍保留，请重试。');
          }
          cloudProgressRevision.current = current.revision;
        }
        const saveId = crypto.randomUUID();
        const saved = await localApi.saveProgress(currentTest.testId!, currentTest.currentQuestionIndex, answersObj, token, {
          save_id: saveId, answer_digest: await progressDigest(answersObj), answered_count: progressAnswerCount(answersObj),
          expected_revision: cloudProgressRevision.current,
          question_ids: questionIds,
        });
        await verifyProgressConfirmation(saved, currentTest.testId!, saveId, answersObj, questionIds);
        cloudProgressRevision.current = saved.revision;
        if (options?.showSuccess) {
          const readBack = await localApi.getSavedProgress(currentTest.testId!, token);
          await verifyProgressConfirmation(readBack, currentTest.testId!, saveId, answersObj, questionIds);
          cloudProgressRevision.current = readBack.revision;
          if (await progressDigest(buildProgressAnswers()) !== await progressDigest(answersObj)) {
            throw new Error('保存期间答案有新修改，已保留本机答案，请再次保存。');
          }
          return readBack;
        }
        return saved;
      });
      progressSaveQueue.current = pending;
      const saved = await pending;
      if (!saved?.success) throw new Error('Progress was not acknowledged');
      if (options?.showSuccess) {
        if (localEditSerial.current !== editSerial) throw new Error('保存期间答案有新修改，已保留本机答案，请再次保存。');
        const savedAt = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
        const count = saved.saved_answer_count;
        setSaveProgressComplete(count === currentTest.questions.length);
        setSaveProgressMessage(`云端已核对 ${count}/${currentTest.questions.length} 题${count < currentTest.questions.length ? `，${currentTest.questions.length - count} 题尚无云端答案` : '，全部已保存'} ${savedAt}`);
      }
      if (options?.showError) {
        setCheckpointError('');
      }
      return true;
    } catch (err) {
      // Refresh the revision without discarding any local input. A subsequent
      // retry can proceed instead of repeating the same stale-version error.
      try {
        const latest = await localApi.getSavedProgress(currentTest.testId!, token);
        if (Number.isInteger(latest.revision) && latest.revision >= 0) cloudProgressRevision.current = latest.revision;
      } catch { /* Keep the local backup until a cloud read succeeds. */ }
      if (options?.showError) {
        setCheckpointError(err instanceof Error ? err.message : '进度保存失败，请检查网络后重试。');
      }
      return false;
    }
  };

  const handleManualSave = async () => {
    if (isSubmitting || isCheckpointSaving || isManualSaving) return;
    setIsManualSaving(true);
    if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current);
    setCheckpointError('');
    try {
      await saveProgress({ showSuccess: true, showError: true });
    } finally {
      setIsManualSaving(false);
    }
  };

  // Auto-save progress (skipped for client-only review/practice sessions)
  useEffect(() => {
    if (!isSubmitting && !isManualSaving && currentTest?.testId && writingSessionId === currentTest.testId && token && !currentTest.testId.startsWith('practice-')) {
      autoSaveTimer.current = setTimeout(() => { void saveProgress(); }, 300);
    }
    return () => { if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current); };
  }, [currentTest?.currentQuestionIndex, currentTest?.answers, writingAnswers, currentTest?.testId, token, writingSessionId, isSubmitting]);

  // Audio follows the current media source and stops when the question or route changes.
  const activeQuestion = currentTest?.questions[currentTest.currentQuestionIndex];
  const questionAudioSrc = activeQuestion?.media && isListeningPart(activeQuestion)
    ? toMediaUrl(activeQuestion.media)
    : '';
  useEffect(() => {
    if (!questionAudioSrc) {
      setAudioElement(null);
      setIsPlaying(false);
      return;
    }

    const audio = new Audio(questionAudioSrc);
    activeAudioRef.current = audio;
    audio.onended = () => {
      if (activeAudioRef.current === audio) setIsPlaying(false);
    };
    audio.onerror = (e) => console.error('Audio load error:', e, questionAudioSrc);
    setAudioElement(audio);
    setIsPlaying(false);

    const autoplayTimer = audioPlayedMap[questionAudioSrc] ? null : window.setTimeout(() => {
      if (activeAudioRef.current !== audio) return;
      audio.play().then(() => {
        if (activeAudioRef.current !== audio) return;
        setIsPlaying(true);
        setAudioPlayedMap(prev => ({ ...prev, [questionAudioSrc]: true }));
      }).catch(err => {
        if (activeAudioRef.current === audio) console.warn('Auto-play prevented:', err);
      });
    }, 500);

    return () => {
      if (autoplayTimer !== null) window.clearTimeout(autoplayTimer);
      const activeAudio = activeAudioRef.current;
      activeAudioRef.current = null;
      for (const instance of activeAudio && activeAudio !== audio ? [activeAudio, audio] : [audio]) {
        instance.onended = null;
        instance.onerror = null;
        instance.pause();
        instance.removeAttribute('src');
      }
    };
    // Recreate audio only when the current question's media or test session changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [questionAudioSrc, currentTest?.testId]);

  // Track group selections for multi-question audio groups
  useEffect(() => {
    if (currentTest && currentTest.questions) {
      // Review Wrong (practice-*) intentionally shows no "已选" hint.
      if (currentTest.testId?.startsWith('practice-')) {
        setGroupSelections([]);
        return;
      }

      const index = currentTest.currentQuestionIndex;
      const q = currentTest.questions[index];
      if (!q) return;

      // HSK 4 L4/R5 use question_group to keep related questions together,
      // but each question has its own options. Their answers are not mutually exclusive.
      if (hasIndependentGroupOptions(q, HSK_LEVEL)) {
        setGroupSelections([]);
        return;
      }

      const questionGroup = getQuestionGroup(q);
      if (!questionGroup) {
        setGroupSelections([]);
        return;
      }

      const usedOptions = currentTest.questions.reduce<number[]>((acc, item, itemIndex) => {
        if (itemIndex === index) return acc;
        if (getQuestionGroup(item) !== questionGroup) return acc;
        const answer = currentTest.answers[itemIndex];
        if (answer !== undefined && answer !== null && answer !== UNKNOWN_ANSWER) {
          acc.push(answer);
        }
        return acc;
      }, []);

      setGroupSelections(usedOptions);
    }
  }, [currentTest?.currentQuestionIndex, currentTest?.answers]);

  // --- Handlers ---
  const handleAnswer = (answerIndex: number) => {
    localEditSerial.current++;
    setSaveProgressMessage('');
    answerQuestion(answerIndex);
  };

  const playAudio = () => {
    if (audioElement && activeAudioRef.current === audioElement && !isPlaying && !audioPlayedMap[audioElement.src]) {
      const audioSrc = audioElement.src;
      audioElement.play().then(() => {
        if (activeAudioRef.current !== audioElement) return;
        setIsPlaying(true);
        setAudioPlayedMap(prev => ({ ...prev, [audioSrc]: true }));
      }).catch(err => console.error('Audio playback failed:', err));
    } else if (!audioElement && !activeAudioRef.current) {
      if (currentTest && currentTest.questions[currentTest.currentQuestionIndex]?.media) {
        const q = currentTest.questions[currentTest.currentQuestionIndex];
        const mediaPath = q.media!;
        const audioSrc = toMediaUrl(mediaPath);
        const audio = new Audio(audioSrc);
        activeAudioRef.current = audio;
        audio.onended = () => setIsPlaying(false);
        audio.onerror = (e) => console.error('Audio load error (retry):', e, audioSrc);
        setAudioElement(audio);
        audio.play().then(() => {
          if (activeAudioRef.current !== audio) return;
          setIsPlaying(true);
          setAudioPlayedMap(prev => ({ ...prev, [audioSrc]: true }));
        }).catch(e => console.error('Retry playback failed', e));
      }
    }
  };

  const onDragStart = (e: React.DragEvent<HTMLDivElement>, i: number) => {
    setDragIndex(i);
    e.dataTransfer.effectAllowed = 'move';
  };

  const onDragOver = (e: React.DragEvent<HTMLDivElement>, overIndex: number) => {
    e.preventDefault();
    if (dragIndex === null) return;
    const idx = currentTest?.currentQuestionIndex || 0;
    const cur = writingAnswersRef.current[idx]?.sequence || [];
    if (dragIndex === overIndex) return;
    const newSeq = [...cur];
    const [moved] = newSeq.splice(dragIndex, 1);
    newSeq.splice(overIndex, 0, moved);
    updateWritingAnswers({ ...writingAnswersRef.current, [idx]: { sequence: newSeq } });
    setDragIndex(overIndex);
  };

  const selectToken = (t: string) => {
    const idx = currentTest?.currentQuestionIndex || 0;
    const cur = writingAnswersRef.current[idx] || {};
    const seq = [...(cur.sequence || []), t];
    updateWritingAnswers({ ...writingAnswersRef.current, [idx]: { sequence: seq } });
  };

  const removeToken = (indexToRemove: number) => {
    const idx = currentTest?.currentQuestionIndex || 0;
    const cur = writingAnswersRef.current[idx]?.sequence || [];
    const newSeq = cur.filter((_, i) => i !== indexToRemove);
    updateWritingAnswers({ ...writingAnswersRef.current, [idx]: { sequence: newSeq } });
  };

  const moveToken = (indexFrom: number, direction: -1 | 1) => {
    const idx = currentTest?.currentQuestionIndex || 0;
    const cur = writingAnswersRef.current[idx]?.sequence || [];
    const target = indexFrom + direction;
    if (target < 0 || target >= cur.length) return;
    const newSeq = [...cur];
    [newSeq[indexFrom], newSeq[target]] = [newSeq[target], newSeq[indexFrom]];
    updateWritingAnswers({ ...writingAnswersRef.current, [idx]: { sequence: newSeq } });
  };

  const onInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const idx = currentTest?.currentQuestionIndex || 0;
    const val = e.target.value;
    updateWritingAnswers({ ...writingAnswersRef.current, [idx]: { text: val } });
  };

  const handleNext = async () => {
    if (isSubmitting || isCheckpointSaving) return;

    if (currentTest && currentTest.currentQuestionIndex < currentTest.questions.length - 1) {
      const isPracticeSession = currentTest.testId?.startsWith('practice-');
      if ((currentTest.currentQuestionIndex + 1) % 10 === 0 && !isPracticeSession) {
        setIsCheckpointSaving(true);
        setCheckpointError('');
        try {
          await saveProgress({ showError: true });
        } catch (err) {
          console.error('Checkpoint save failed:', err);
          setCheckpointError('网络连接异常，进度保存失败，请检查网络后重试。');
        } finally {
          setIsCheckpointSaving(false);
          nextQuestion();
        }
      } else {
        nextQuestion();
      }
    } else {
      handleComplete();
    }
  };

  const handleComplete = async () => {
    if (!currentTest || isSubmitting || writingSessionId !== currentTest.testId) return;
    if (!token || !currentTest.testId) {
      setError('提交失败，请检查网络');
      return;
    }
    setIsSubmitting(true);
    if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current);
    setError('');
    setSaveProgressMessage('');

    const testId = currentTest.testId || cloudSessionId;
    const payload = currentTest.questions.map((q, idx) => {
      if (isSequenceQuestion(q)) {
        const wa = writingAnswersRef.current[idx] || {};
        return {
          question_id: q.id,
          chosen_option_index: null,
          chose_buzhidao: false,
          time_spent_seconds: 30,
          chosen_sequence: (wa.sequence || []).length > 0 ? wa.sequence! : null,
        };
      }
      if (isTextEntryQuestion(q)) {
        const wa = writingAnswersRef.current[idx] || {};
        return {
          question_id: q.id,
          chosen_option_index: null,
          chose_buzhidao: false,
          time_spent_seconds: 30,
          chosen_text_answer: (wa.text || '').trim().length > 0 ? (wa.text || '').trim() : null,
        };
      }
      const ans = currentTest.answers[idx];
      return {
        question_id: q.id,
        chosen_option_index: ans === UNKNOWN_ANSWER ? null : ans,
        chose_buzhidao: ans === UNKNOWN_ANSWER,
        time_spent_seconds: 30,
      };
    });

    try {
      await progressSaveQueue.current.catch(() => undefined);
      const response = isCloudflare
        ? await api.submitTest(testId!, payload, token)
        : await localApi.submitTest(testId!, payload, token);

      const historyConfirmed = await waitForHistoryRecord(testId!, token);
      if (!historyConfirmed) throw new Error('History record not visible yet');

      setScoreSummary({
        total: Number(response.weighted_score || 0),
        max: Number(response.max_score || 300),
        listening: Number(response.section_scores?.listening || 0),
        reading: Number(response.section_scores?.reading || 0),
        writing: Number(response.section_scores?.writing || 0),
      });
      clearCurrentTest();
      clearPersistedWritingAnswers(testId);
      updateWritingAnswers({});
      setShowResults(true);
    } catch (err: any) {
      console.error('Submission error:', err);
      setError(err?.message || '提交失败，请检查网络');
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    // Navigation
    navigate,
    sessionReady: Boolean(writingSessionId && writingSessionId === currentTest?.testId),
    resumeChoices,
    chooseResume: (id: string) => setChoiceState({ path: location.pathname, id }),
    chooseNewSimulation: () => setChoiceState({ path: location.pathname, id: 'new' }),
    // Config
    HSK_LEVEL,
    currentConfig,
    UNKNOWN_ANSWER,
    shouldShowPinyin,
    // State
    loading,
    initializingApi,
    error,
    showResults,
    scoreSummary,
    currentTest,
    isPlaying,
    audioElement,
    audioPlayedMap,
    groupSelections,
    writingAnswers,
    isSubmitting,
    isCheckpointSaving,
    isManualSaving,
    checkpointError,
    saveProgressMessage,
    saveProgressComplete,
    // Type helpers
    isListeningPart,
    isReadingPart,
    isWritingPart,
    isSequenceQuestion,
    isTextEntryQuestion,
    isLongFormWritingQuestion,
    isVerticalSequenceQuestion,
    // Path helpers
    isImagePath,
    extractImagePath,
    resolveQuestionImagePath,
    stripImagePathFromText,
    toMediaUrl,
    getTokens,
    getTokenCounts,
    renderText,
    // Actions
    handleAnswer,
    handleNext,
    handleManualSave,
    previousQuestion,
    playAudio,
    selectToken,
    removeToken,
    moveToken,
    onInputChange,
    onDragStart,
    onDragOver,
  };
}

export type SimulationController = ReturnType<typeof useSimulationController>;
